'use client'

import { useEffect } from 'react'
import { useForm, useStore } from '@tanstack/react-form'
import { z } from 'zod/v4'

import type { Locale } from '@helper/utils/types'
import { Badge, Button, Field, FieldError, FieldLabel, FieldSet, Input } from '@ui/web/components'
import { tryCatch } from '@helper/utils/core'
import cn from '@config/tailwind/cn'

const BankAccountFormSchema = (locale: Locale) =>
  z.object({
    bankName: z
      .string()
      .min(3, locale === 'id' ? 'Nama bank harus diisi minimal 3 karakter' : 'Bank name must be at least 3 characters'),
    accountNumber: z
      .string()
      .min(
        10,
        locale === 'id' ? 'Nomor rekening harus minimal 10 karakter' : 'Account number must be at least 10 characters',
      )
      .regex(/^\d+$/, locale === 'id' ? 'Nomor rekening harus berupa angka' : 'Account number must be a number'),
    accountHolderName: z
      .string()
      .min(
        3,
        locale === 'id'
          ? 'Nama pemegang akun harus diisi minimal 3 karakter'
          : 'Account holder name must be at least 3 characters',
      ),
    bankBranch: z.string().nullable().optional(),
    swiftCode: z.string().nullable().optional(),
  })

export type BankAccountFormValues = z.infer<ReturnType<typeof BankAccountFormSchema>>

interface BankAccountFormProps {
  onSubmit: (values: BankAccountFormValues) => Promise<void> | void
  initialValues?: Partial<BankAccountFormValues>
  submitButtonText?: string
  submitButtonClassName?: string
  className?: string
  locale?: Locale
}

export function BankAccountForm({
  onSubmit,
  initialValues = {},
  submitButtonText: submitButtonTextProp,
  submitButtonClassName,
  className,
  locale = 'id',
}: BankAccountFormProps) {
  const submitButtonText = submitButtonTextProp ?? (locale === 'id' ? 'Simpan' : 'Save')

  const form = useForm({
    defaultValues: {
      bankName: initialValues.bankName ?? '',
      accountNumber: initialValues.accountNumber ?? '',
      accountHolderName: initialValues.accountHolderName ?? '',
      bankBranch: initialValues.bankBranch ?? null,
      swiftCode: initialValues.swiftCode ?? null,
    } as z.input<ReturnType<typeof BankAccountFormSchema>>,
    validators: {
      onSubmit: BankAccountFormSchema(locale),
    },
    onSubmit: async ({ value, formApi }) => {
      const [_, error] = await tryCatch(async () => await onSubmit(value))

      if (error) {
        // @ts-expect-error - error.message is a string
        formApi.setErrorMap({ onSubmit: error.message })
      }
    },
  })

  useEffect(() => {
    void form.validateAllFields('change')
  }, [])

  const errorMap = useStore(form.store, (state) => state.errorMap)
  const canSubmit = useStore(form.store, (state) => state.canSubmit)
  const isSubmitting = useStore(form.store, (state) => state.isSubmitting)

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        e.stopPropagation()
        void form.handleSubmit()
      }}
      className={className}
    >
      <FieldSet>
        <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-8'>
          <h6 className='srd:text-xl srd:font-medium'>{locale === 'id' ? 'Detail Bank' : 'Bank Details'}</h6>

          <div className='srd:w-full srd:flex srd:flex-col srd:gap-y-4'>
            <form.Field name='bankName'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                return (
                  <Field className='srd:gap-1' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>{locale === 'id' ? 'Nama Lengkap Bank' : 'Bank Name'}</FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      placeholder={locale === 'id' ? 'Nama lengkap bank' : 'Full bank name'}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>

            <form.Field name='accountNumber'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                return (
                  <Field className='srd:gap-1' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      {locale === 'id' ? 'Nomor Rekening Bank' : 'Bank Account Number'}
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      placeholder='1234567890'
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>

            <form.Field name='accountHolderName'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                return (
                  <Field className='srd:gap-1' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      {locale === 'id' ? 'Nama Pemegang Akun Bank' : 'Bank Account Holder Name'}
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value}
                      placeholder='PT PIBO MEDIA ANAK'
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value)}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>

            <form.Field name='bankBranch'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                return (
                  <Field className='srd:gap-1' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      {locale === 'id' ? 'Cabang Bank' : 'Bank Branch'}{' '}
                      <Badge>{locale === 'id' ? 'Opsional' : 'Optional'}</Badge>
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value ?? ''}
                      placeholder='Cabang bank'
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value || null)}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>

            <form.Field name='swiftCode'>
              {(field) => {
                const isInvalid = field.state.meta.isTouched && !field.state.meta.isValid

                return (
                  <Field className='srd:gap-1' data-invalid={isInvalid}>
                    <FieldLabel htmlFor={field.name}>
                      {locale === 'id' ? 'Kode SWIFT' : 'SWIFT Code'}{' '}
                      <Badge>{locale === 'id' ? 'Opsional' : 'Optional'}</Badge>
                    </FieldLabel>
                    <Input
                      id={field.name}
                      name={field.name}
                      value={field.state.value ?? ''}
                      placeholder={locale === 'id' ? 'Kode SWIFT' : 'SWIFT code'}
                      onBlur={field.handleBlur}
                      onChange={(e) => field.handleChange(e.target.value || null)}
                      aria-invalid={isInvalid}
                    />
                    {isInvalid && <FieldError errors={field.state.meta.errors} />}
                  </Field>
                )
              }}
            </form.Field>
          </div>
        </div>
      </FieldSet>

      {errorMap?.onSubmit && typeof errorMap.onSubmit === 'string' ? (
        <p className='srd:text-main-red-100 srd:mt-4 srd:text-xs'>{errorMap.onSubmit}</p>
      ) : null}

      <Button
        type='submit'
        disabled={!canSubmit}
        loading={Boolean(isSubmitting)}
        className={cn('srd:bg-main-blue-100 srd:mt-8 srd:w-full srd:rounded-full', submitButtonClassName)}
      >
        {submitButtonText}
      </Button>
    </form>
  )
}
